import pluralize from "pluralize";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Card } from "widgets";

import style from "./styles/UserRolesAdmin.module.scss";

import { matchFilter, parseFilterExpression } from "../../app-utils/filter.js";
import { type ISelectOption } from "../../app-utils/form.js";
import { type ISelectionModel, createSelectionModel, isSelected } from "../../app-utils/selection-model.js";
import { capitalizeFirstLetter, pluralizeWord } from "../../app-utils/string.js";
import { type Capability, type Role, type User } from "../../graphql.js";
import { type IIdentifiable, type ITableColumn } from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import { SelectItem, SelectWidget } from "../core/forms/SelectWidget.js";
import DataContextIndicator from "../core/grid/DataContextIndicator.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import { CELL_TYPE, SORT_ORDER_ASC } from "@/settings/settings.js";
import { useAsyncFilter } from "@/ui-modules/core/forms/hooks/useAsyncFilter.js";

// TODO remove the related component
export type ISelectUserOption = ISelectOption;

const roleUserColumnDefs: Array<Partial<ITableColumn>> = [
  {
    name: "Username",
    field: "user.username",
    cellType: CELL_TYPE.STRING,
    sort: { direction: SORT_ORDER_ASC.toLowerCase(), priority: null },
  },
  {
    name: "First Name",
    field: "user.firstName",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Last Name",
    field: "user.lastName",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "User Groups",
    field: "user.userGroups.name",
    cellType: CELL_TYPE.STRING_LIST,
  },
  {
    name: "Roles",
    field: "user.roles.name",
    cellType: CELL_TYPE.STRING_LIST,
  },
];

const useCreateUserGridData = (
  users: User[],
  selectionModel: ISelectionModel,
  showAssignedOnly: boolean,
  filterTerm: string,
): IIdentifiable[] => {
  return useMemo(() => {
    return users
      .filter((user: User) => (showAssignedOnly ? isSelected(selectionModel, user.id) : true))
      .filter((user: User) =>
        matchFilter(
          [
            user.username,
            user.firstName,
            user.lastName,
            ...(user.userGroups?.map((userGroup) => userGroup.name) || []),
            ...(user.roles?.map((role) => role.name) || []),
          ],
          parseFilterExpression(filterTerm),
        ),
      )
      .map((user: User) => {
        return {
          id: user.id,
          assigned: !!selectionModel.selectedIds.find((selectedId) => selectedId === user.id),
          user: { ...user }, // we have to clone the user because of a bug in ag-grid 13 (AG-771)
        };
      });
  }, [users, filterTerm, showAssignedOnly, selectionModel]);
};

export interface IRoleUsersAdminCardProps {
  role: Role;
  isLoading?: boolean;
  selectionModelUpdated?: (selModel: ISelectionModel) => void;
}

export function RoleUsersAdminCard(props: IRoleUsersAdminCardProps) {
  const users = useSelector<IAppState, User[]>((state) => state.usersAdmin.users);
  const [filterTerm, setFilterTerm] = useAsyncFilter(null);
  const [showAssignedOnly, setShowAssignedOnly] = useState<boolean>(!props.selectionModelUpdated);

  const selectionModel = useMemo(() => {
    const selectionModel = createSelectionModel(props.role.users.map((user) => user.id));
    props.selectionModelUpdated?.(selectionModel);
    return selectionModel;
  }, [props.role]);

  const rowData: IIdentifiable[] = useCreateUserGridData(users, selectionModel, showAssignedOnly, filterTerm);

  return (
    <RoleAdminCard
      key={`role-admin-card-${props.role.name}`}
      entityLabel={"user"}
      columnsDefs={roleUserColumnDefs}
      rowData={rowData}
      fullListCount={users.length}
      isLoading={props.isLoading}
      filterTerm={filterTerm}
      setFilterTerm={setFilterTerm}
      selectionModelUpdated={props.selectionModelUpdated}
      selectionModel={selectionModel}
      showAssignedOnly={showAssignedOnly}
      setShowAssignedOnly={setShowAssignedOnly}
    />
  );
}

const rolePlaybookColumnDefs: Array<Partial<ITableColumn>> = [
  {
    name: "Name",
    field: "playbookCapability.name",
    cellType: CELL_TYPE.STRING,
    sort: { direction: SORT_ORDER_ASC.toLowerCase(), priority: null },
  },
  {
    name: "Suffix",
    field: "playbookCapability.nameSuffix",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Description",
    field: "playbookCapability.description",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Type",
    field: "playbookCapability.type.name",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Roles",
    field: "playbookCapability.roles.name",
    cellType: CELL_TYPE.STRING_LIST,
  },
  {
    name: "Categories",
    field: "playbookCapability.categories.name",
    cellType: CELL_TYPE.STRING_LIST,
  },
  {
    name: "Status",
    field: "playbookCapability.enabled",
    cellType: CELL_TYPE.BOOLEAN,
  },
];

const useCreatePlaybookGridData = (
  playbooks: Capability[],
  selectionModel: ISelectionModel,
  showAssignedOnly: boolean,
  filterTerm: string,
): IIdentifiable[] => {
  return useMemo(() => {
    return playbooks
      .filter((capability: Capability) => (showAssignedOnly ? isSelected(selectionModel, capability.id) : true))
      ?.filter((capability: Capability) =>
        matchFilter(
          [
            capability.playbook.name,
            capability.playbook.description,
            capability.playbook.type.name,
            ...(capability.playbook.roles?.map((role) => role.name) || []),
            ...(capability.playbook.categories?.map((category) => category.name) || []),
          ],
          parseFilterExpression(filterTerm),
        ),
      )
      .map((capability: Capability) => {
        return {
          id: capability.id,
          assigned: !!selectionModel.selectedIds.find((selectedId) => selectedId === capability.id),
          playbookCapability: capability.playbook,
        };
      });
  }, [playbooks, filterTerm, showAssignedOnly, selectionModel]);
};

export interface IRolePlaybooksDisplayCardProps {
  role: Role;
  isLoading?: boolean;
  selectionModelUpdated?: (selModel: ISelectionModel) => void;
}

export function RolePlaybooksAdminCard(props: IRolePlaybooksDisplayCardProps) {
  const capabilities = useSelector<IAppState, Capability[]>((state) => state.rolesAdmin.playbooks);
  const [filterTerm, setFilterTerm] = useAsyncFilter(null);
  const [showAssignedOnly, setShowAssignedOnly] = useState<boolean>(!props.selectionModelUpdated);

  const selectionModel = useMemo(() => {
    const userCapabilities = props.role.capabilities.filter((capability) => capability.type === "playbook");
    const selectionModel = createSelectionModel(userCapabilities.map((capability) => capability.id));
    props.selectionModelUpdated?.(selectionModel);
    return selectionModel;
  }, [props.role]);

  const rowData: IIdentifiable[] = useCreatePlaybookGridData(
    capabilities,
    selectionModel,
    showAssignedOnly,
    filterTerm,
  );

  return (
    <RoleAdminCard
      key={`role-admin-card-${props.role.name}`}
      entityLabel={"playbook"}
      columnsDefs={rolePlaybookColumnDefs}
      rowData={rowData}
      fullListCount={capabilities.length}
      isLoading={props.isLoading}
      filterTerm={filterTerm}
      setFilterTerm={setFilterTerm}
      selectionModelUpdated={props.selectionModelUpdated}
      selectionModel={selectionModel}
      showAssignedOnly={showAssignedOnly}
      setShowAssignedOnly={setShowAssignedOnly}
    />
  );
}

const roleCapabilityColumnDefs: Array<Partial<ITableColumn>> = [
  {
    name: "Name",
    field: "capability.name",
    cellType: CELL_TYPE.STRING,
    sort: { direction: SORT_ORDER_ASC.toLowerCase(), priority: null },
  },
  {
    name: "Description",
    field: "capability.description",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Group Name",
    field: "capability.groupName",
    //rowGroup: true,
    cellType: CELL_TYPE.STRING,
  },
];

const useCreateCapabilitiesGridData = (
  capabilities: Capability[],
  selectionModel: ISelectionModel,
  showAssignedOnly: boolean,
  filterTerm: string,
): IIdentifiable[] => {
  return useMemo(() => {
    return capabilities
      .filter((capability: Capability) => (showAssignedOnly ? isSelected(selectionModel, capability.id) : true))
      ?.filter((capability: Capability) =>
        matchFilter([capability.name, capability.description, capability.groupName], parseFilterExpression(filterTerm)),
      )
      .map((capability: Capability) => {
        return {
          id: capability.id,
          assigned: !!selectionModel.selectedIds.find((selectedId) => selectedId === capability.id),
          capability: { ...capability }, // we have to clone the user because of a bug in ag-grid 13 (AG-771)
        };
      });
  }, [capabilities, filterTerm, showAssignedOnly, selectionModel]);
};

export interface IRoleCapabilitiesAdminCardProps {
  role: Role;
  isLoading?: boolean;
  selectionModelUpdated?: (selModel: ISelectionModel) => void;
}

export function RoleCapabilitiesAdminCard(props: IRoleCapabilitiesAdminCardProps) {
  const capabilities = useSelector<IAppState, Capability[]>((state) => state.rolesAdmin.capabilities);
  const [filterTerm, setFilterTerm] = useAsyncFilter(null);
  const [showAssignedOnly, setShowAssignedOnly] = useState<boolean>(!props.selectionModelUpdated);

  const selectionModel = useMemo(() => {
    const userCapabilities = props.role.capabilities.filter((capability) => capability.type !== "playbook");
    const selectionModel = createSelectionModel(userCapabilities.map((capability) => capability.id));
    props.selectionModelUpdated?.(selectionModel);
    return selectionModel;
  }, [props.role]);

  const rowData: IIdentifiable[] = useCreateCapabilitiesGridData(
    capabilities,
    selectionModel,
    showAssignedOnly,
    filterTerm,
  );

  return (
    <RoleAdminCard
      key={`role-admin-card-${props.role.name}`}
      entityLabel={"capability"}
      columnsDefs={roleCapabilityColumnDefs}
      rowData={rowData}
      fullListCount={capabilities.length}
      isLoading={props.isLoading}
      filterTerm={filterTerm}
      setFilterTerm={setFilterTerm}
      selectionModelUpdated={props.selectionModelUpdated}
      selectionModel={selectionModel}
      showAssignedOnly={showAssignedOnly}
      setShowAssignedOnly={setShowAssignedOnly}
    />
  );
}

interface IRoleAdminCardProps {
  entityLabel: string;
  columnsDefs: Array<Partial<ITableColumn>>;
  rowData: IIdentifiable[];
  fullListCount: number;
  filterTerm: string;
  isLoading: boolean;
  setFilterTerm: (expr: string, _) => Promise<void>;
  selectionModelUpdated?: (selModel: ISelectionModel) => void;
  selectionModel?: ISelectionModel;
  showAssignedOnly: boolean;
  setShowAssignedOnly: (showAssignedOnly: boolean) => void;
}

export function RoleAdminCard(props: IRoleAdminCardProps) {
  const [colDefs, setColDefs] = useState<Array<Partial<ITableColumn>>>(props.columnsDefs);

  useEffect(() => {
    if (!props.selectionModelUpdated) {
      setColDefs([
        {
          name: "Assigned",
          field: "assigned",
          cellType: CELL_TYPE.BOOLEAN,
          fixed: true,
        },
        ...props.columnsDefs,
      ]);
    }
  }, []);

  const allKey = "allKey";
  const assignedOnlyKey = "assignedOnlyKey";
  const selectedKey = props.showAssignedOnly ? assignedOnlyKey : allKey;

  const selectionCount = props.selectionModel.selectedIds.length;
  return (
    <Card
      title={pluralize(capitalizeFirstLetter(props.entityLabel))}
      subtitle={`${selectionCount} ${pluralizeWord(props.entityLabel, selectionCount)} assigned out of ${
        props.fullListCount
      }`}
      fillHeight
      className={style.fill_height}
      contentClassName={style.grid_card_content}
      actions={[
        <SelectWidget
          icon={"account_box"}
          label={" "}
          selectionMode={"single"}
          key={"show-assigned-widget"}
          selectedKeys={[selectedKey]}
          onSelectionChange={(selection) => {
            props.setShowAssignedOnly(String([...selection][0]) === assignedOnlyKey);
          }}
        >
          <SelectItem key={allKey}>All</SelectItem>
          <SelectItem key={assignedOnlyKey}>Assigned</SelectItem>
        </SelectWidget>,
        <AsyncSearchWithExprParser
          onSearchExprChange={props.setFilterTerm}
          controlledValue={props.filterTerm}
          key={"role-admin-page-widget-1"}
        />,
      ]}
    >
      {props.isLoading ? null : ( // important to reinitialize the selection model that might have changed
        <InMemoryTableWithSelectionModel
          columns={colDefs}
          rowGridData={props.rowData}
          useSelectionModel={!!props.selectionModelUpdated}
          uniqueGridIdentifier={`role-${props.entityLabel}`}
          initialSelectionModel={props.selectionModel}
          selectionModelUpdated={props.selectionModelUpdated}
          className={"no-border-grid"}
          isLoading={props.isLoading}
          noRowsMessage={"No assignments"}
        />
      )}
      <DataContextIndicator
        key="context-indicator"
        numTotalRows={props.fullListCount}
        numRowsFiltered={props.rowData.length}
        numRowsSelected={props.selectionModel?.selectedIds.length}
        rowEntityLabel={props.entityLabel}
        filterIsSet={!!props.filterTerm?.length}
        onResetFilter={() => props.setFilterTerm("", "")}
        selectionVerb={"assigned"}
      />
    </Card>
  );
}
